<template>
  <nd-modal
    v-model:show="isMainModalShown"
    :card-class="'w-full h-dvh flex flex-col md:w-440px md:pb-10 md:h-auto'"
    :closable="isCloseButtonShown"
    :mask-closable="authStep !== 'reset-password'"
    @before-close="handleBeforeClose"
  >
    <div class="w-33 mx-auto mb-2">
      <nd-img src="@/assets/images/logo.svg" alt="Niceday Logo" class="text-yellow-50" />
    </div>
    <client-only>
      <div class="px-5 pb-5 md:px-10 md:pb-0 flex-1 flex flex-col">
        <component :is="authStepComponent" ref="authStepRef" />
      </div>
    </client-only>
  </nd-modal>
  <content-viewer
    :show="isNonMainModalShown"
    :show-modal-header="false"
    :mask-closable="false"
    :with-trigger-btn="false"
    :modal-class="'md:w-440px'"
    @before-close="handleNonMainModalBeforeClose"
  >
    <component :is="authLayerComponent" ref="authLayerRef" />
    <template #footer>
      <div class="flex gap-2" :class="isMobileOrTablet ? 'flex-1 px-5 py-3 shadow-mobile-footer' : 'px-5 py-6 md:justify-end md:rounded-b-lg'">
        <nd-button v-if="showCancelButton" :class="{ 'w-full': isMobileOrTablet }" type="tertiary" @click="handleDialogCancel">
          {{ dialogCancelText }}
        </nd-button>
        <nd-button
          :class="{ 'w-full': isMobileOrTablet }"
          type="primary"
          :disabled="isDialogConfirmDisabled"
          :loading="authLayerRef?.isLoading"
          @click="handleDialogConfirm"
        >
          {{ dialogConfirmText }}
        </nd-button>
      </div>
    </template>
  </content-viewer>
</template>
<script setup lang="ts">
import { storeToRefs } from 'pinia'
import type { ComputedRef } from 'vue'
import NdModal from '../common/nd-modal.vue'
import ContentViewer from '../common/content-viewer.vue'
import LoginMenu from './login-menu.vue'
import LoginStep from './login-step.vue'
import RegisterStep from './register-step.vue'
import ForgotPasswordStep from './forgot-password-step.vue'
import ResetPassword from './reset-password.vue'
import ThirdPartyConsentStep from './third-party-consent-step.vue'
import ConfirmDiscardLayer from './confirm-discard-layer.vue'

import { useAuthStore } from '@/store/auth'
import { useAppStore } from '@/store/app'
import { QueryStringKeys } from '@/constants/commons'

const authStore = useAuthStore()
const appStore = useAppStore()
const route = useRoute()
const router = useLocaleRouter()
const { t } = useI18n()

const { isLogin, authStep, isLoginModalShown } = storeToRefs(authStore)

const loginPendingAction = useLoginPendingActionCookie()

const authStepRef = ref<{ shouldConfirmClose?: ComputedRef<boolean> } | null>(null)
const authLayerRef = ref<InstanceType<typeof ThirdPartyConsentStep> | null>(null)

const isMobileOrTablet = computed(() => appStore.isMobile || appStore.isTablet)

const isMainModalShown = computed({
  get: () => authStore.authLayer === 'main' && isLoginModalShown.value,
  set: value => {
    isLoginModalShown.value = value
  },
})

const isNonMainLayerShown = computed(() => authStore.authLayer !== 'main')
const isNonMainModalShown = computed(() => isNonMainLayerShown.value && isLoginModalShown.value)

const isCloseButtonShown = computed(() => authStep.value !== 'reset-password')
const showCancelButton = computed(() => authStore.authLayer !== 'third-party-consent')

const isDialogConfirmDisabled = computed(() =>
  authStore.authLayer === 'third-party-consent' ? authLayerRef.value?.isSubmitDisabled || authLayerRef.value?.isLoading : false
)

const dialogCancelText = computed(() => t('all_return'))

const dialogConfirmText = computed(() =>
  authStore.authLayer === 'third-party-consent' ? t('log_in_create_account_button') : t('log_in_create_account_cancel_modal_cancel_button')
)

const authStepComponent = computed(() => {
  switch (authStep.value) {
    case 'sign-up':
      return RegisterStep
    case 'login':
      return LoginStep
    case 'forgot-password':
      return ForgotPasswordStep
    case 'reset-password':
      return ResetPassword
    case 'login-menu':
    default:
      return LoginMenu
  }
})

const authLayerComponent = computed(() => {
  switch (authStore.authLayer) {
    case 'confirm-discard':
      return ConfirmDiscardLayer
    case 'third-party-consent':
      return ThirdPartyConsentStep
    default:
      return null
  }
})

watch(isLoginModalShown, newVal => {
  if (!newVal) {
    authStore.closeLoginModal()
    clearEffects()
  }
})

const handleDialogCancel = () => {
  if (authStore.authLayer === 'confirm-discard') {
    authStore.cancelConfirmClose()
  } else if (authStore.authLayer === 'third-party-consent') {
    authStore.closeLoginModal()
  }
}

const handleConfirmDiscard = () => {
  if (authStore.previousLayer !== 'third-party-consent') {
    authStore.confirmCloseLoginModal()
    return
  }

  const fallbackUrl = authStore.pendingThirdPartyAuth?.fallbackUrl || '/'
  const redirectUrl = authStore.pendingThirdPartyAuth?.redirectUrl
  const url = new URL(fallbackUrl, location.origin)
  if (redirectUrl) url.searchParams.set(QueryStringKeys.REDIRECT_URL, redirectUrl)

  authStore.confirmCloseLoginModal()
  router.replace(url.pathname + url.search)
}

const handleDialogConfirm = () => {
  if (authStore.authLayer === 'confirm-discard') {
    handleConfirmDiscard()
  } else if (authStore.authLayer === 'third-party-consent') {
    authLayerRef.value?.handleSubmit?.()
  }
}

/**
 * 主層 Modal 關閉前處理
 * - 檢查 register-step 是否有未保存的內容
 * - 如果有，進入 confirm-discard 層，阻止 modal 關閉
 * - 否則，允許 modal 關閉
 */
const handleBeforeClose = (preventClose: () => void) => {
  const computedRef = authStepRef.value?.shouldConfirmClose
  const shouldConfirmClose = computedRef ? unref(computedRef) : false

  if (shouldConfirmClose) {
    const prevented = authStore.tryCloseLoginModal(true)
    if (prevented) {
      preventClose()
    }
  }
}

/**
 * 非主層 Modal 關閉前處理
 * - confirm-discard 層：取消關閉，返回上一層；若返回的是非主層則阻止 modal 關閉
 * - third-party-consent 層：進入 confirm-discard 層，阻止 modal 關閉
 */
const handleNonMainModalBeforeClose = (preventClose: () => void) => {
  if (authStore.tryCloseNonMainLayer()) {
    preventClose()
  }
}

const clearEffects = () => {
  if (isLogin.value) return

  if (route.query[QueryStringKeys.REDIRECT_URL]) {
    const copiedQuery = cloneDeep(route.query)

    delete copiedQuery[QueryStringKeys.REDIRECT_URL]
    router.replace({ query: copiedQuery })
  }
  loginPendingAction.value = null
}
</script>

<style lang="scss" scoped>
.shadow-mobile-footer {
  box-shadow: 0px 2px 10px 0px rgba(0, 0, 0, 0.1);
}
</style>
